import { Box, ListItemText, Stack, Tooltip, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import React, { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import Avatar from "../Avatar";
import UploadImage from "./uploadImage";
import { localStorageInstance } from "../../utils/localStorage";
import { hasImage, removeHtmlTagsAndEntities } from "../../utils";
import { useDispatch } from "react-redux";
import { searchBoard } from "../../redux/SearchSlice";
import _ from "lodash";
import { isAdmin } from "../../utils/constants";
import { changeExpand } from "../../redux/boardListSlice";
import { setSideBarDrawer } from "../../redux/Sidebar";
import MergeBoard from "../../pages/board/components/mergeBoard/MergeBoard";
import "./header.scss";
import { useCustomSearchParams } from "../../hooks/useCustomSearchParams";
import { useResponsive } from "../../hooks/useResponsive";
import { FeedbackDialogContent } from "./FeedBackDialogContent.js";
import { TooltipName } from "../../utils/Enums.js";
import CustomMenu from "../Menu/index.js";
import CustomMenuItem from "../Menu/CustomMenuItem.js";
import FormDialog from "../Dialog/FormDialog.js";
import { CustomButton } from "../Button/Button.js";
import { schema } from "../../schemas/feedbackSchema.js";
import { ProfileMenuItem } from "./components/ProfileMenuItem.js";
import { profileMenuList } from "./components/menuList.js";

function Profile() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const user = useSelector((state) => state.users.logInUser);
  const dispatch = useDispatch();
  const isMobile = useResponsive("down", "sm");
  const [openMergeBoard, setOpenMergeBoard] = useState(false);
  const userName =
    Object.hasOwn(user, "first_name") &&
    `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isLogoutLoading, setIsLogoutLoading] = useState(false);
  const [showFeedBackDialog, setShowFeedBackDialog] = useState(false);
  const [boardParam, setBoardParam, removeBoardParam] =
    useCustomSearchParams("find-brd");

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleLogout = useCallback((action) => {
    setIsLogoutLoading(true);
    sessionStorage.getItem("userId")
      ? window.close()
      : localStorageInstance.clear();
    queryClient.clear();
    navigate(`/`);
    setIsLogoutLoading(false);
  }, [navigate, queryClient]);

  const handleClose = useCallback((action) => {
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    // setInputValue(boardParam || '');
    dispatch(searchBoard(boardParam || ""));
    if (isAdmin && boardParam) {
      dispatch(changeExpand("allPanel"));
    }
  }, []);

  const handleNavigate = useCallback(
    (boardNumber) => {
      const today = new Date();
      const yesterday = new Date();

      // Subtract 7 days from the current date
      yesterday.setDate(today.getDate() - 1);

      const date = {
        startDate: yesterday,
        endDate: yesterday,
        key: "selection",
      };

      const newSearchParams = new URLSearchParams({
        tab: "worklog",
        board: boardNumber,
        epic: null,
        missingStatus: JSON.stringify(date),
      });
      navigate(`/admin-report?${newSearchParams.toString()}`);
      setAnchorEl(null);
      dispatch(setSideBarDrawer(false));
    },
    [navigate, dispatch]
  );

  const handleFeedBackForm = useCallback(() => {
    document.getElementById("feedbackForm").requestSubmit();
  }, []);

  const handleMenuClick = useCallback(
    (menuItem) => {
      switch (menuItem.id) {
        case "feedback":
          setShowFeedBackDialog(true);
          break;
        case "missingStatus":
          handleNavigate();
          break;
        case "mergeBoard":
          setOpenMergeBoard(true);
          break;
        case "logout":
          setShowLogoutDialog(true);
          break;
        default:
          if (menuItem.path) {
            navigate(menuItem.path);
            dispatch(setSideBarDrawer(false));
          }
      }
      setAnchorEl(null);
    },
    [navigate, dispatch]
  );

  const handleCloseLogoutDialog = useCallback(() => {
    setShowLogoutDialog(false);
  }, []);

  const handleCloseMergeBoard = useCallback(() => {
    setOpenMergeBoard(false);
  }, []);

  const handleCloseFeedBackDialog = useCallback(() => {
    setShowFeedBackDialog(false);
  }, []);

  return (
    <>
      {showLogoutDialog && (
        <FormDialog
          open={showLogoutDialog}
          handleClose={handleCloseLogoutDialog}
          loading={isLogoutLoading}
          handleSubmit={handleLogout}
          buttonTxt="Logout"
          title="Confirmation: Log Out"
          cancelBtn={true}
          alignActions={"center"}
          fullWidth={true}>
          <Typography className="align_text_center mb-2">
            Are you sure you want to log out?
          </Typography>
        </FormDialog>
      )}

      {showFeedBackDialog && (
        <FormDialog
          open={showFeedBackDialog}
          handleClose={handleCloseFeedBackDialog}
          loading={isLogoutLoading}
          handleSubmit={handleFeedBackForm}
          buttonTxt="Submit"
          cancelBtn={true}
          width={"800px"}
          className="feedback-form-dialog"
          title="TaskBoard Beta – We’d Love Your Feedback!">
          <FeedbackDialogContent
            schema={schema}
            handleClose={handleCloseFeedBackDialog}
          />
        </FormDialog>
      )}
      {openMergeBoard && (
        <MergeBoard
          close={handleCloseMergeBoard}
          open={openMergeBoard}
          admin={true}
        />
      )}
      <div className="header-right">
        {Object.hasOwn(user, "first_name") && (
          <Box>
            <Stack direction="row" justifyContent={"end"}>
              <Tooltip title={TooltipName.PROFILE.PROFILE}>
                <CustomButton
                  className="profile-btn"
                  onClick={handleClick}
                  endIcon={
                    <KeyboardArrowDownIcon className="profile-down-arrow" />
                  }>
                  <Avatar
                    title={`${user.first_name} ${user.last_name}`}
                    user_image={user?.user_image}
                    className="user-avatar"
                    style={
                      !hasImage(user?.user_image) && {
                        bgcolor: user?.profile_color?.background,
                        color: user?.profile_color?.text,
                      }
                    }
                    isDisable={true}>
                    {userName}
                  </Avatar>
                  {!isMobile && (
                    <div>
                      <Typography className="user-name">
                        {user?.first_name} {user.last_name}
                      </Typography>
                    </div>
                  )}
                </CustomButton>
              </Tooltip>
            </Stack>

            <CustomMenu
              id="account-menu"
              className="profile-header"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}>
              <CustomMenuItem disableRipple sx={{ cursor: "default" }}>
                <Stack className="profile-container">
                  <UploadImage
                    user={user}
                    userName={userName}
                    setAnchorEl={setAnchorEl}
                  />

                  <ListItemText
                    className="ml-2"
                    primary={
                      <React.Fragment>
                        <Typography className="profile-dropdown-name">
                          {user.first_name} {user.last_name}
                        </Typography>
                      </React.Fragment>
                    }
                    secondary={
                      <React.Fragment>
                        <Typography className="profile-dropdown-email">
                          {user.email}
                        </Typography>
                      </React.Fragment>
                    }
                  />
                </Stack>
              </CustomMenuItem>
              {profileMenuList.map((item, index) => (
                <ProfileMenuItem
                  key={`profile-menu-${index}`}
                  menuItem={item}
                  handleMenuClick={handleMenuClick}
                />
              ))}
            </CustomMenu>
          </Box>
        )}
      </div>
    </>
  );
}

export default React.memo(Profile);
